import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { TemplateState } from '../../../../types/common/types';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { MAX_PORT_VALUE, MIN_PORT_VALUE, UseProxyFormViewMetadata } from '../types';
import { GzInputWebComponent } from '../../../../ui/gzInput/component/GzInputWebComponent';
import { MaxValidator } from '../../../../ui/gzForm/validators/max.validator';
import { MinValidator } from '../../../../ui/gzForm/validators/min.validator';
import { CustomizableValidator } from '../../../../ui/gzForm/validators/customizable.validator';
import { PackageData } from '../../../types/package-data';
import { ValidationResult } from '../../../../ui/gzForm/formField/types/form-validator';
import { translateFn } from '../../../../utils/translator.helper';
import { PackageActionContext } from '../../types/PackageActionContext';

export class PackageUseProxyFormView extends BaseWebComponentView {
  public formGroup!: FormGroup;
  private packageData: PackageData;

  public dom: {
    $wrapper: HTMLElement;
    $proxySectionChecked: HTMLElement;
    $useProxyCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

    $serverProxyFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $portProxyFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $usernameProxyFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $passwordProxyFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  };

  getDomElements(): void {
    this.dom = {
      $wrapper: this.shadow.querySelector('#package-use-proxy-form-wrapper'),
      $proxySectionChecked: this.shadow.querySelector('#use-proxy-checked-section'),

      $useProxyCheckbox: this.shadow.querySelector('#use-proxy'),

      $serverProxyFormField: this.shadow.querySelector('#server-proxy-form-field'),
      $portProxyFormField: this.shadow.querySelector('#port-proxy-form-field'),
      $usernameProxyFormField: this.shadow.querySelector('#username-proxy-form-field'),
      $passwordProxyFormField: this.shadow.querySelector('#password-proxy-form-field'),
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations(): void {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings(): void {
    this.dom.$useProxyCheckbox.Input.label = translateFn('Use proxy for communication');

    this.dom.$serverProxyFormField.label = translateFn('Server');
    this.dom.$serverProxyFormField.Input.placeholder = translateFn('Type here');

    this.dom.$portProxyFormField.label = translateFn('Port');
    this.dom.$portProxyFormField.Input.placeholder = translateFn('Type here');

    this.dom.$usernameProxyFormField.label = translateFn('Username');
    this.dom.$usernameProxyFormField.Input.placeholder = translateFn('Type here');

    this.dom.$passwordProxyFormField.label = translateFn('Password');
    this.dom.$passwordProxyFormField.Input.placeholder = translateFn('Type here');
  }

  buildSuccessView(viewMetadata: UseProxyFormViewMetadata): void {
    this.packageData = viewMetadata.packageData;
    this.createFormGroup();
    if (viewMetadata.context === PackageActionContext.Edit) {
      this.formGroup.setDisabled(false);
    }
    if (this.packageData) {
      this.setFormValuesFromDatabase(this.packageData);
    }
  }

  buildErrorView(error: Error): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private createFormGroup(): void {
    this.formGroup = FormGroup.build({
      settings: {
        proxy: {
          enable: this.dom.$useProxyCheckbox,
          server: this.dom.$serverProxyFormField,
          port: this.dom.$portProxyFormField,
          username: this.dom.$usernameProxyFormField,
          password: this.dom.$passwordProxyFormField,
        },
      },
    });
    this.formGroup.setDisabled(true);
  }

  public addValidationPortServerUserPass(state: boolean): void {
    if (state) {
      this.dom.$serverProxyFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
      this.dom.$portProxyFormField.Validators = [
        RequiredValidator(this.commonStrings.requiredField),
        MaxValidator(MAX_PORT_VALUE, translateFn('Please specify a port number between 1 and 65535')),
        MinValidator(MIN_PORT_VALUE, translateFn('Please specify a port number between 1 and 65535')),
      ];
    } else {
      this.dom.$serverProxyFormField.Validators = [];
      this.dom.$portProxyFormField.Validators = [];
    }

    this.addUsernameValidation(state);
    this.addPasswordValidation(state);

    if (this.packageData?.settings?.proxy?.username) {
      this.formGroup.validate(true);
    } else if (this.packageData?.settings?.proxy?.server) {
      this.dom.$portProxyFormField.validate(true);
      this.dom.$serverProxyFormField.validate(true);
    }
  }

  private addPasswordValidation(state: boolean): void {
    if (state) {
      this.dom.$passwordProxyFormField.Validators = [
        CustomizableValidator(this.customValidationRequiredPassword.bind(this)),
      ];
    } else {
      this.dom.$passwordProxyFormField.Validators = [];
    }
  }

  private addUsernameValidation(state: boolean): void {
    if (state) {
      this.dom.$usernameProxyFormField.Validators = [
        CustomizableValidator(this.customValidationRequiredUsername.bind(this)),
      ];
    } else {
      this.dom.$usernameProxyFormField.Validators = [];
    }
  }

  public setFormValuesFromDatabase(packageData: PackageData): void {
    this.formGroup.value = {
      settings: {
        proxy: {
          server: packageData.settings.proxy?.server,
          port: packageData.settings.proxy?.port,
          username: packageData.settings.proxy?.username,
          password: packageData.settings.proxy?.password,
          enable: packageData.settings.proxy.enable,
        },
      },
    };
  }

  public resetProxyFieldsState(): void {
    this.dom.$useProxyCheckbox.value = false;
    this.dom.$serverProxyFormField.value = '';
    this.dom.$serverProxyFormField.resetField();
    this.dom.$portProxyFormField.value = '';
    this.dom.$portProxyFormField.resetField();
    this.dom.$usernameProxyFormField.value = '';
    this.dom.$usernameProxyFormField.resetField();
    this.dom.$passwordProxyFormField.value = '';
    this.dom.$passwordProxyFormField.resetField();
  }

  private customValidationRequiredPassword(): ValidationResult {
    if (this.dom.$usernameProxyFormField.value !== '' && this.dom.$passwordProxyFormField.value === '') {
      this.dom.$usernameProxyFormField.setAttribute('required', 'required');
      this.dom.$passwordProxyFormField.setAttribute('required', 'required');

      return {
        valid: false,
        error: translateFn('Required field'),
      };
    }

    this.handleNoValueRequiredUserPass();
    return {
      valid: true,
    };
  }

  private customValidationRequiredUsername(): ValidationResult {
    if (this.dom.$passwordProxyFormField.value !== '' && this.dom.$usernameProxyFormField.value === '') {
      this.dom.$usernameProxyFormField.setAttribute('required', 'required');
      this.dom.$passwordProxyFormField.setAttribute('required', 'required');

      return {
        valid: false,
        error: translateFn('Required field'),
      };
    }

    this.handleNoValueRequiredUserPass();
    return {
      valid: true,
    };
  }

  private handleNoValueRequiredUserPass(): void {
    if (this.dom.$usernameProxyFormField.value === '' && this.dom.$passwordProxyFormField.value === '') {
      this.dom.$usernameProxyFormField.removeAttribute('required');
      this.dom.$passwordProxyFormField.removeAttribute('required');
    }
  }
}
